import { useEffect, useRef, useState } from "react";
import Icon from "../../../components/base/Icon";
import { toastError } from "../../../components/base/Toast";
import PhoneInput from "../../../components/form/PhoneInput";
import { deviceToken, useHub, useSocket } from "../../../core/Global";
import PhoneItem from "../../../models/PhoneItem";
import TwilioLine from "../../voice/lines/models/TwilioLine";
import SignupActions from "./SignupActions";
import "./SignupNumberSettings.css";


type Destination = "extension" | "queue" | "external"

const DESTINATIONS: { key: Destination, icon: any, title: string, text: string }[] = [
    { key: "extension", icon: "phone-office", title: "An extension", text: "Calls ring one phone. We create the extension for you." },
    { key: "queue", icon: "users", title: "A call queue", text: "Calls wait in a queue and ring your team. We create the queue with an extension on it." },
    { key: "external", icon: "arrow-right-arrow-left", title: "Redirect to a number", text: "Calls are forwarded to another phone number." }
]

// What setup goes through for each choice, and the percentage the hub reports when it starts
// each part -- the same figures CompleteSetup sends.
const STAGES: Record<Destination, { label: string, at: number }[]> = {
    extension: [
        { label: "Reserving your phone number", at: 0 },
        { label: "Creating your extension", at: 35 },
        { label: "Connecting your number to the extension", at: 70 },
        { label: "Finishing up", at: 95 }
    ],
    queue: [
        { label: "Reserving your phone number", at: 0 },
        { label: "Creating your call queue", at: 30 },
        { label: "Creating your extension", at: 50 },
        { label: "Adding the extension to the queue", at: 62 },
        { label: "Connecting your number to the queue", at: 75 },
        { label: "Finishing up", at: 95 }
    ],
    external: [
        { label: "Reserving your phone number", at: 0 },
        { label: "Setting up the redirect", at: 60 },
        { label: "Finishing up", at: 95 }
    ]
}


// The wizard's number step: where calls to the chosen number go.
//
// Nothing has been bought yet. Finishing here buys the number, creates the extension or the
// queue the chosen route needs, sets the route and marks the account set up -- all in one request,
// which takes a while because the phone system is reconfigured along the way. While it runs the
// step itself turns into the progress: the hub reports each stage as it goes, the buttons are
// disabled, and leaving the page asks first.
//
// Each choice stands on its own. Names come from the company and everything finer is set from the
// console later; the only thing asked for is the number to redirect to, which cannot be guessed.
export default function SignupNumberSettings(props: {
    token?: string
    line: TwilioLine
    numberType?: string
    onBack: () => void
    onSkipSetup: () => void
    onDone: () => void
}) {

    const hub = useHub()
    const socket = useSocket()

    const [destination, destinationSet] = useState<Destination>("extension")
    const [redirectPhone, redirectPhoneSet] = useState<PhoneItem>()

    // While the number is being set up: how far it has got, in the hub's own words.
    const [working, workingSet] = useState<boolean>(false)
    const [percent, percentSet] = useState<number>(0)
    const [reached, reachedSet] = useState<number>(0)
    const serverPercent = useRef<number>(0)

    // The bar keeps creeping between the hub's reports, so a long stage still looks alive -- but
    // never past the next report by much, and never to the end on its own.
    useEffect(() => {
        if (!working) return
        const t = setInterval(() => {
            percentSet(p => Math.min(p + 1, Math.min(serverPercent.current + 12, 92)))
        }, 900)
        return () => clearInterval(t)
    }, [working])

    // Closing or leaving the page in the middle would leave the number half set up, so the
    // browser asks first.
    useEffect(() => {
        if (!working) return
        const warn = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = "Your phone number is still being set up. Leaving now will interrupt it."
            return e.returnValue
        }
        window.addEventListener("beforeunload", warn)
        return () => window.removeEventListener("beforeunload", warn)
    }, [working])

    const finish = () => {
        window.hideFormErrors()

        if (destination == "external" && !redirectPhone?.phone) {
            window.showFormError("redirectToNumber", "Enter the number calls should go to.")
            return
        }

        workingSet(true)
        serverPercent.current = 3
        reachedSet(3)
        percentSet(3)

        // The hub reports each stage to this device on the app-loader channel.
        const token = deviceToken() ?? ""
        const key = socket.start(skt => {
            skt.invoke("StartListenToAppLoader", token).then(() => {
                skt.on("UpdateAppLoaderStatus", (p: number) => {
                    serverPercent.current = p
                    reachedSet(p)
                    percentSet(cur => Math.max(cur, p))
                })
            })
        }, skt => {
            skt.off("UpdateAppLoaderStatus")
            skt.invoke("StopListenToAppLoader", token)
        })

        const stopListening = () => { try { socket.stop(key) } catch { } }

        const failed = (message: string, field?: string) => {
            stopListening()
            workingSet(false)
            // Beside the field it is about when that field is on the page; otherwise as a toast.
            if (field && document.getElementById(field + "-err")) window.showFormError(field, message)
            else toastError(message)
        }

        hub.Post("/auth/signup/completesetup", {
            token: props.token,
            phoneNumber: props.line.phoneNumber,
            friendlyName: props.line.friendlyName,
            numberType: props.numberType ?? "1",
            destination,
            redirectToNumber: redirectPhone?.phone,
            redirectToCountryCode: redirectPhone?.countryCode ?? "+1"
        }, res => {
            if (!res.success) { failed(res.message ?? "Setup could not be finished. Please try again.", res.field); return }

            stopListening()
            percentSet(100)
            reachedSet(100)
            workingSet(false)
            props.onDone()
        }, () => failed("The connection was lost while setting up. Please try again -- nothing is set up twice."))
    }

    const stages = STAGES[destination]
    const current = stages.reduce((last, s, i) => (reached >= s.at ? i : last), 0)

    return (
        <div className="w-100 signup-number-settings">

            {
                working ?

                    <div className="signup-setup-progress">
                        <div className="signup-setup-icon">
                            <Icon icon="phone-volume" type="solid" size={42} />
                        </div>
                        <h1 className="fw-bolder fs-2qx text-gray-800 mb-3">Setting up your phone number</h1>
                        <div className="text-gray-600 fs-5 mb-10">
                            <b className="text-gray-800">{props.line.friendlyName}</b> is being connected.
                            This can take up to a minute — please keep this page open.
                        </div>

                        <div className="signup-setup-bar">
                            <div className="signup-setup-bar-fill" style={{ width: percent + "%" }}></div>
                        </div>
                        <div className="signup-setup-percent">{percent}%</div>

                        <div className="signup-setup-stages">
                            {
                                stages.map((s, i) =>
                                    <div key={s.label} className={"signup-setup-stage" + (i < current ? " is-done" : i == current ? " is-current" : "")}>
                                        <span className="signup-setup-stage-icon">
                                            {
                                                i < current ? <Icon icon="check" type="solid" size={12} />
                                                    : i == current ? <Icon icon="spinner-third" spin size={13} />
                                                        : <Icon icon="circle" size={8} />
                                            }
                                        </span>
                                        {s.label}
                                    </div>
                                )
                            }
                        </div>
                    </div>

                    :

                    <>
                        <div className="mb-8 text-center">
                            <Icon type="thin" icon="phone-volume" size={80} className="text-primary mb-6" />
                            <h1 className="fw-bolder fs-2qx text-gray-800 mb-4">Where should calls go?</h1>
                            <div className="text-gray-600 fs-6">
                                Your new number <b className="text-gray-800">{props.line.friendlyName}</b> is reserved for you.
                                Choose who answers it.
                            </div>
                        </div>

                        <div className="signup-dest-options mb-8">
                            {
                                DESTINATIONS.map(d =>
                                    <button key={d.key} type="button"
                                        className={"signup-dest-option" + (destination == d.key ? " is-selected" : "")}
                                        onClick={e => { e.preventDefault(); destinationSet(d.key); window.hideFormErrors() }}>
                                        <Icon icon={d.icon} type="solid" size={22} className="signup-dest-icon" />
                                        <div className="signup-dest-title">{d.title}</div>
                                        <div className="signup-dest-text">{d.text}</div>
                                    </button>
                                )
                            }
                        </div>

                        {
                            destination == "external" &&
                            <div className="signup-dest-form">
                                <PhoneInput id="phone-signup-redirect" name="redirectToNumber" label="Redirect calls to" labelUp block
                                    countryCodes="us-only"
                                    onComplete={e => redirectPhoneSet(e)}
                                    onInComplete={() => redirectPhoneSet(undefined)} />
                            </div>
                        }
                    </>
            }

            <SignupActions>
                <button type="button" className="btn btn-light" onClick={props.onBack} disabled={working}>
                    <Icon icon="chevron-left" type="solid" className="me-2" />
                    Back
                </button>
                <button type="button" className="btn btn-light ms-3" onClick={props.onSkipSetup} disabled={working}>
                    Skip setup &amp; go to console
                </button>
                <button type="button" className="btn btn-lg btn-primary min-w-200px ms-auto" disabled={working} onClick={finish}>
                    {
                        working ?
                            <>
                                <span className="indicator-label me-2">Setting up</span>
                                <Icon icon="spinner-third" spin />
                            </>
                            :
                            <>
                                <span className="indicator-label me-2">Finish setup</span>
                                <Icon icon="check" type="solid" />
                            </>
                    }
                </button>
            </SignupActions>
        </div>
    )
}
